import { MapContainer, TileLayer, Polygon, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import React, { useEffect, useMemo } from "react";
import { convertGeoJsonToLeaflet } from "../utils/coordinateConversion";
import { MAP_TILE_CONFIG } from "@racoon/lib";

// Component to handle map view updates
const MapViewUpdater = ({ bounds }: { bounds: L.LatLngBounds | null }) => {
  const map = useMap();

  useEffect(() => {
    if (map && bounds) {
      // Fit map to bounds with padding
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);

  return null;
};

interface RecordsMapViewProps {
  records: any[];
  recordType?: string;
  onMarkerClick?: (record: any) => void;
}

export const RecordsMapView: React.FC<RecordsMapViewProps> = ({ records, onMarkerClick }) => {
  // Calculate bounds from all locations
  const boundsData = useMemo(() => {
    const allCoords: [number, number][] = [];

    records.forEach((record) => {
      // Try to get coordinates from geometry
      if (record.geometry && record.geometry.coordinates) {
        const coords = record.geometry.coordinates[0]; // Get outer ring
        if (Array.isArray(coords)) {
          // Convert from GeoJSON [lon, lat] to Leaflet [lat, lon]
          const convertedCoords = convertGeoJsonToLeaflet(coords);
          convertedCoords.forEach((pair) => {
            allCoords.push(pair);
          });
        }
      }
    });
    if (allCoords.length === 0) {
      // Default to Almaty, Kazakhstan if no records
      return {
        bounds: null,
        center: [43.238949, 76.940088] as [number, number],
      };
    }

    const bounds = L.latLngBounds(allCoords);
    const center = bounds.getCenter();

    return {
      bounds,
      center: [center.lat, center.lng] as [number, number],
    };
  }, [records]);

  return (
    <MapContainer
      center={boundsData.center}
      zoom={13}
      style={{ height: "100%", width: "100%", borderRadius: "8px" }}
      className="rounded-[8px] z-0"
      zoomControl={false}
    >
      <TileLayer
        url={MAP_TILE_CONFIG.url}
        subdomains={MAP_TILE_CONFIG.subdomains}
        attribution={MAP_TILE_CONFIG.attribution}
      />
      <MapViewUpdater bounds={boundsData.bounds} />

      {records.map((record1) => {
        const record = record1.location;
        // Get coordinates from geometry
        if (!record.geometry || !record.geometry.coordinates) {
          return null;
        }

        const coords = record.geometry.coordinates[0]; // Get outer ring
        if (!Array.isArray(coords) || coords.length === 0) {
          return null;
        }

        // Convert from GeoJSON [lon, lat] to Leaflet [lat, lon]
        const positions = convertGeoJsonToLeaflet(coords);

        if (positions.length === 0) {
          return null;
        }

        return (
          <Polygon
            key={record.id}
            positions={positions}
            pathOptions={{
              color: "#DC2626",
              fill: true,
              fillColor: "#FCA5A5",
              fillOpacity: 0.5,
              weight: 2,
            }}
          >
            <Popup closeButton={false}>
              <div
                className="text-sm font-semibold cursor-pointer hover:underline"
                onClick={() => {
                  onMarkerClick?.(record);
                }}
              >
                {record.name}
              </div>
            </Popup>
          </Polygon>
        );
      })}
    </MapContainer>
  );
};
